export const SHOPIFY_STATUSES = [
  'ACTIVE',
  'ARCHIVED',
  'SUPPLIER_DISCONTINUING',
  'DERANGING',
] as const;

export type ShopifyStatus = (typeof SHOPIFY_STATUSES)[number];

/** Default report filter: active variants only. */
export const DEFAULT_SHOPIFY_STATUSES: ShopifyStatus[] = ['ACTIVE'];

/** Overstock and Markdowns default: every Shopify status except Archived. */
export const DEFAULT_OVERSTOCK_SHOPIFY_STATUSES: ShopifyStatus[] = SHOPIFY_STATUSES.filter(
  (status) => status !== 'ARCHIVED'
);

const SHOPIFY_STATUS_LOOKUP: Record<string, ShopifyStatus> = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
  SUPPLIER_DISCONTINUING: 'SUPPLIER_DISCONTINUING',
  DERANGING: 'DERANGING',
};

function toLookupKey(value: string): string {
  return value.trim().toUpperCase().replace(/[\s-]+/g, '_');
}

export function isShopifyStatus(value: string): value is ShopifyStatus {
  return (SHOPIFY_STATUSES as readonly string[]).includes(value.toUpperCase());
}

export function normalizeShopifyStatus(value: string | null | undefined): ShopifyStatus {
  if (value == null || value.trim() === '') {
    return 'ACTIVE';
  }
  const key = toLookupKey(value);
  return SHOPIFY_STATUS_LOOKUP[key] ?? 'ACTIVE';
}

export function isKnownShopifyStatusInput(value: string | null | undefined): boolean {
  if (value == null || value.trim() === '') {
    return true;
  }
  return toLookupKey(value) in SHOPIFY_STATUS_LOOKUP;
}

export function normalizeShopifyStatuses(values: readonly string[]): ShopifyStatus[] {
  const normalized = values
    .map((s) => s.toUpperCase())
    .filter((s): s is ShopifyStatus => isShopifyStatus(s));
  return normalized.length > 0 ? normalized : [...DEFAULT_SHOPIFY_STATUSES];
}

/** Button label for variant status filter popovers. */
export function shopifyStatusLabel(statuses: readonly ShopifyStatus[]): string {
  if (statuses.length === 0) return 'Variant status';
  if (statuses.length === SHOPIFY_STATUSES.length) return 'Variant: All statuses';
  if (statuses.length === 1) return `Variant: ${formatShopifyStatus(statuses[0]!)}`;
  return `Variant: ${statuses.map((status) => formatShopifyStatus(status)).join(', ')}`;
}

/**
 * Exact ProductVariant `custom.status` metafield labels written to Shopify.
 * Distinct from `formatShopifyStatus`, which is sentence-case UI copy.
 */
export const SHOPIFY_STATUS_METAFIELD_VALUES: Record<ShopifyStatus, string> = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
  SUPPLIER_DISCONTINUING: 'Supplier Discontinuing',
  DERANGING: 'Deranging',
};

export function shopifyStatusMetafieldValue(status: ShopifyStatus): string {
  return SHOPIFY_STATUS_METAFIELD_VALUES[status];
}

export function formatShopifyStatus(status: string | null | undefined): string {
  if (!status) return 'Active';
  switch (status.toUpperCase()) {
    case 'ACTIVE':
      return 'Active';
    case 'ARCHIVED':
      return 'Archived';
    case 'SUPPLIER_DISCONTINUING':
      return 'Supplier discontinuing';
    case 'DERANGING':
      return 'Deranging';
    default:
      return status.charAt(0) + status.slice(1).toLowerCase();
  }
}

/** Compact pill: Archived → A, Supplier discontinuing / Deranging → D, Active → none. */
export function shopifyStatusLetter(
  status: string | null | undefined
): { letter: 'D' | 'A'; label: string } | null {
  const key = status?.trim().toUpperCase();
  if (key === 'ARCHIVED') return { letter: 'A', label: formatShopifyStatus('ARCHIVED') };
  if (key === 'SUPPLIER_DISCONTINUING') {
    return { letter: 'D', label: formatShopifyStatus('SUPPLIER_DISCONTINUING') };
  }
  if (key === 'DERANGING') return { letter: 'D', label: formatShopifyStatus('DERANGING') };
  return null;
}
